import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchAll } from "../lib/api";
import ArticleCard from "../components/ArticleCard";
import ComparisonCard from "../components/ComparisonCard";
import ProductCard from "../components/ProductCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { useNoindex } from "../lib/seo";

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState({ articles: [], comparisons: [], products: [], categories: [] });
  const [loading, setLoading] = useState(false);
  useNoindex(true);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    searchAll(q)
      .then(setResults)
      .finally(() => setLoading(false));
  }, [q]);

  const total =
    results.articles.length +
    results.comparisons.length +
    results.products.length +
    results.categories.length;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="search-results">
      <Breadcrumbs items={[{ label: "Search" }]} />
      <header className="mb-12 max-w-3xl">
        <p className="eyebrow mb-3">Search</p>
        <h1 className="font-playfair text-4xl sm:text-5xl text-neutral-900 tracking-tight leading-[1.05] mb-3">
          Results for <span className="italic text-indigo-800">"{q}"</span>
        </h1>
        <p className="text-neutral-600">
          {loading ? "Searching…" : `${total} results found.`}
        </p>
      </header>

      {results.categories.length > 0 && (
        <section className="mb-14">
          <h2 className="font-playfair text-2xl mb-5">Categories</h2>
          <ul className="flex flex-wrap gap-3">
            {results.categories.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/categories/${c.slug}`}
                  className="inline-flex px-4 py-2 rounded-full bg-surface border border-neutral-200 text-sm hover:border-indigo-800 hover:text-indigo-800 transition-colors"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {results.products.length > 0 && (
        <section className="mb-14">
          <h2 className="font-playfair text-2xl mb-5">Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {results.articles.length > 0 && (
        <section className="mb-14">
          <h2 className="font-playfair text-2xl mb-5">Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {results.articles.map((a, i) => (
              <ArticleCard
                key={a.id}
                article={a}
                basePath={a.type === "review" ? "/reviews" : "/guides"}
                index={i}
              />
            ))}
          </div>
        </section>
      )}

      {results.comparisons.length > 0 && (
        <section className="mb-14">
          <h2 className="font-playfair text-2xl mb-5">Comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {results.comparisons.map((c) => <ComparisonCard key={c.id} comparison={c} />)}
          </div>
        </section>
      )}

      {!loading && total === 0 && q && (
        <p className="text-neutral-500" data-testid="search-empty">
          No results found. Try a different search term.
        </p>
      )}
    </div>
  );
}
